"use client";

import { getCribnoshAvatarUrl, resolveCribnoshAvatarBaseUrl } from "@shared/avatars/defaultAvatars";
import { UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { getProfileAvatarFallbackSrc, resolveAvatarCandidate } from "@/lib/profile-avatar";
import { cn } from "@/lib/utils";

type WebAvatarProps = {
  src?: string | null;
  alt: string;
  cribnoshAvatarId?: string | null;
  fallbackSrc?: string | null;
  showLoadingIndicator?: boolean;
  fill?: boolean;
  className?: string;
  imageClassName?: string;
  iconClassName?: string;
};

export function WebAvatar({
  src,
  alt,
  cribnoshAvatarId,
  fallbackSrc,
  showLoadingIndicator = true,
  fill = false,
  className,
  imageClassName,
  iconClassName,
}: WebAvatarProps) {
  const primarySrc = useMemo(() => resolveAvatarCandidate(src), [src]);
  const resolvedFallbackSrc = useMemo(() => {
    const explicitFallback = resolveAvatarCandidate(fallbackSrc);
    if (explicitFallback) return explicitFallback;
    if (cribnoshAvatarId) {
      return getCribnoshAvatarUrl(cribnoshAvatarId, resolveCribnoshAvatarBaseUrl(process.env));
    }
    return getProfileAvatarFallbackSrc();
  }, [cribnoshAvatarId, fallbackSrc]);
  const [hasPrimaryError, setHasPrimaryError] = useState(false);
  const [hasFallbackError, setHasFallbackError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setHasPrimaryError(false);
    setIsLoading(true);
  }, [primarySrc]);

  useEffect(() => {
    setHasFallbackError(false);
    setIsLoading(true);
  }, [resolvedFallbackSrc]);

  const frameClassName = cn(
    fill ? "absolute inset-0 h-full w-full" : "relative block h-full w-full",
  );

  const activeSrc =
    primarySrc && !hasPrimaryError
      ? primarySrc
      : !hasFallbackError
        ? resolvedFallbackSrc
        : null;

  const activeFitClassName =
    primarySrc && !hasPrimaryError ? "object-cover" : "object-contain";

  if (activeSrc) {
    return (
      <span className={cn(frameClassName, "overflow-hidden", className)}>
        <img
          alt={alt}
          className={cn("h-full w-full", activeFitClassName, imageClassName)}
          src={activeSrc}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            if (primarySrc && !hasPrimaryError) {
              setHasPrimaryError(true);
              setIsLoading(true);
              return;
            }
            setHasFallbackError(true);
            setIsLoading(false);
          }}
        />
        {showLoadingIndicator && isLoading ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 animate-pulse bg-[linear-gradient(135deg,rgba(244,244,245,0.92),rgba(228,228,231,0.75),rgba(244,244,245,0.92))]"
          />
        ) : null}
      </span>
    );
  }

  return (
    <span className={cn(frameClassName, className, "flex items-center justify-center", iconClassName)}>
      <UserRound className="h-[55%] w-[55%] text-[#56625D]" strokeWidth={2.25} />
    </span>
  );
}
